import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ─── Honduras Timezone Helpers ────────────────────────────────────────────────
// Honduras is UTC-6 (no DST). We must never use toISOString() for local business
// dates because a sale at 11 PM Honduras time is midnight UTC the next day.

/**
 * Returns the current date in Honduras local time as "YYYY-MM-DD". * Use this everywhere a"today" date is needed for the business.
 */
export function getHondurasDateStr(date?: Date): string {
  const d = date || new Date();
  // Honduras is UTC-6 (no DST)
  const hondurasOffset = -6 * 60; // minutes
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  const hondurasMs = utcMs + hondurasOffset * 60000;
  const hd = new Date(hondurasMs);
  const y = hd.getFullYear();
  const m = String(hd.getMonth() + 1).padStart(2, '0');
  const day = String(hd.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Returns the current time in Honduras local time as "HH:MM".
 */
export function getHondurasTimeStr(date?: Date): string {
  const d = date || new Date();
  const hondurasOffset = -6 * 60;
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  const hondurasMs = utcMs + hondurasOffset * 60000;
  const hd = new Date(hondurasMs);
  return `${String(hd.getHours()).padStart(2, '0')}:${String(hd.getMinutes()).padStart(2, '0')}`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GastoOperativo {
  id: string;
  name: string;
  category: string;
  description: string;
  amount: number;
  expense_date: string;
  invoice_number?: string;
  notes?: string;
  payment_account_id?: string;
  funding_source?: 'business_account' | 'external_family' | 'owner_personal';
  created_at?: string;
}

export interface PurchaseOrder {
  id: string;
  po_number: string;
  supplier: string;
  order_date: string;
  delivery_date?: string;
  status: 'draft' | 'ordered' | 'received' | 'cancelled';
  notes?: string;
  subtotal: number;
  tax: number;
  total: number;
  received_at?: string;
  inventory_updated: boolean;
  is_historical?: boolean;
  payment_account_id?: string;
  funding_source?: 'business_account' | 'external_family' | 'owner_personal';
  invoice_reference?: string;
  created_at?: string;
  items?: PurchaseOrderItem[];
}

export interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  ingredient_id: string;
  ingredient_name: string;
  unit: string;
  quantity_ordered: number;
  quantity_received: number;
  unit_price: number;
  subtotal: number;
  item_type: 'ingredient' | 'packaging_material';
}

// ─── Ingredient (Supabase) ────────────────────────────────────────────────────

export interface IngredientRow {
  id: string;
  name: string;
  unit: 'g' | 'ml' | 'unidad';
  bag_content: number;
  bag_price: number;
  unit_cost: number;
  dose: number;
  dose_cost: number;
  stock: number;
  min_stock: number;
  provider: string;
  last_updated: string;
  status: 'ok' | 'low' | 'critical' | 'out';
  created_at?: string;
}

export const ingredientesService = {
  getAll: async (): Promise<IngredientRow[]> => {
    const { data, error } = await supabase
      .from('ingredients')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as IngredientRow[];
  },

  create: async (ing: Omit<IngredientRow, 'id' | 'created_at'>): Promise<IngredientRow> => {
    const id = `ing-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('ingredients')
      .insert([{ ...ing, id }])
      .select()
      .single();
    if (error) throw error;
    return data as IngredientRow;
  },

  update: async (id: string, ing: Partial<IngredientRow>): Promise<IngredientRow> => {
    const { data, error } = await supabase
      .from('ingredients')
      .update(ing)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as IngredientRow;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase.from('ingredients').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Products (Supabase) ──────────────────────────────────────────────────────

export interface ProductRow {
  id: string;
  name: string;
  product_type: string;
  image_emoji: string;
  active: boolean;
  pedidos_ya_commission: number;
  total_cost: number;
  price_direct: number;
  price_whatsapp_instagram: number;
  price_pedidosya: number;
  price_other: number;
  created_at?: string;
}

export interface ProductSizeRecipeRow {
  id: string;
  product_id: string;
  size_label: string;
  ingredient_id: string;
  ingredient_name: string;
  dose: number;
  unit: string;
  unit_cost: number;
  dose_cost: number;
}

export interface ProductSizePriceRow {
  id: string;
  product_id: string;
  size_label: string;
  price_direct: number;
  price_whatsapp_instagram: number;
  price_pedidosya: number;
  price_other: number;
}

export interface RecipeIngredientRow {
  id: string;
  product_id: string;
  ingredient_id: string;
  ingredient_name: string;
  dose: number;
  unit: string;
  unit_cost: number;
  dose_cost: number;
}

export const productosService = {
  getAll: async (): Promise<ProductRow[]> => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as ProductRow[];
  },

  getSizeRecipes: async (productId: string): Promise<ProductSizeRecipeRow[]> => {
    const { data, error } = await supabase
      .from('product_size_recipes')
      .select('*')
      .eq('product_id', productId);
    if (error) throw error;
    return (data || []) as ProductSizeRecipeRow[];
  },

  getSizePrices: async (productId: string): Promise<ProductSizePriceRow[]> => {
    const { data, error } = await supabase
      .from('product_size_prices')
      .select('*')
      .eq('product_id', productId);
    if (error) throw error;
    return (data || []) as ProductSizePriceRow[];
  },

  getRecipeIngredients: async (productId: string): Promise<RecipeIngredientRow[]> => {
    const { data, error } = await supabase
      .from('recipe_ingredients')
      .select('*')
      .eq('product_id', productId);
    if (error) throw error;
    return (data || []) as RecipeIngredientRow[];
  },

  create: async (
    product: Omit<ProductRow, 'id' | 'created_at'>,
    sizeRecipes: Omit<ProductSizeRecipeRow, 'id' | 'product_id'>[],
    sizePrices: Omit<ProductSizePriceRow, 'id' | 'product_id'>[],
    recipeIngredients: Omit<RecipeIngredientRow, 'id' | 'product_id'>[]
  ): Promise<ProductRow> => {
    const id = `prod-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('products')
      .insert([{ ...product, id }])
      .select()
      .single();
    if (error) throw error;

    if (sizeRecipes.length > 0) {
      const rows = sizeRecipes.map((r) => ({
        ...r,
        id: `psr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: id,
      }));
      await supabase.from('product_size_recipes').insert(rows);
    }

    if (sizePrices.length > 0) {
      const rows = sizePrices.map((r) => ({
        ...r,
        id: `psp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: id,
      }));
      await supabase.from('product_size_prices').insert(rows);
    }

    if (recipeIngredients.length > 0) {
      const rows = recipeIngredients.map((r) => ({
        ...r,
        product_id: id,
      }));
      await supabase.from('recipe_ingredients').insert(rows);
    }

    return data as ProductRow;
  },

  update: async (
    id: string,
    product: Partial<ProductRow>,
    sizeRecipes: Omit<ProductSizeRecipeRow, 'id' | 'product_id'>[],
    sizePrices: Omit<ProductSizePriceRow, 'id' | 'product_id'>[],
    recipeIngredients: Omit<RecipeIngredientRow, 'id' | 'product_id'>[]
  ): Promise<ProductRow> => {
    const { data, error } = await supabase
      .from('products')
      .update(product)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    // Replace all related data
    await supabase.from('product_size_recipes').delete().eq('product_id', id);
    await supabase.from('product_size_prices').delete().eq('product_id', id);
    await supabase.from('recipe_ingredients').delete().eq('product_id', id);

    if (sizeRecipes.length > 0) {
      const rows = sizeRecipes.map((r) => ({
        ...r,
        id: `psr-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: id,
      }));
      await supabase.from('product_size_recipes').insert(rows);
    }

    if (sizePrices.length > 0) {
      const rows = sizePrices.map((r) => ({
        ...r,
        id: `psp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        product_id: id,
      }));
      await supabase.from('product_size_prices').insert(rows);
    }

    if (recipeIngredients.length > 0) {
      const rows = recipeIngredients.map((r) => ({
        ...r,
        product_id: id,
      }));
      await supabase.from('recipe_ingredients').insert(rows);
    }

    return data as ProductRow;
  },

  delete: async (id: string): Promise<void> => {
    await supabase.from('product_size_recipes').delete().eq('product_id', id);
    await supabase.from('product_size_prices').delete().eq('product_id', id);
    await supabase.from('recipe_ingredients').delete().eq('product_id', id);
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Staff / User Profiles (Supabase) ─────────────────────────────────────────

export interface StaffMemberRow {
  id: string;
  name: string;
  username: string;
  password: string;
  role: 'admin' | 'cajero' | 'inventario';
  commission_rate: number;
  perm_pos: boolean;
  perm_reportes: boolean;
  perm_ingredientes: boolean;
  active: boolean;
  created_at?: string;
}

export const perfilesService = {
  getAll: async (): Promise<StaffMemberRow[]> => {
    const { data, error } = await supabase
      .from('staff_members')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as StaffMemberRow[];
  },

  create: async (staff: Omit<StaffMemberRow, 'id' | 'created_at'>): Promise<StaffMemberRow> => {
    const id = `staff-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('staff_members')
      .insert([{ ...staff, id }])
      .select()
      .single();
    if (error) throw error;
    return data as StaffMemberRow;
  },

  update: async (id: string, staff: Partial<StaffMemberRow>): Promise<StaffMemberRow> => {
    const { data, error } = await supabase
      .from('staff_members')
      .update(staff)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as StaffMemberRow;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase.from('staff_members').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Sales (Supabase) ─────────────────────────────────────────────────────────

export interface SaleRow {
  id: string;
  receipt_number: string;
  sale_date: string;
  sale_time: string;
  cashier: string;
  customer_name: string;
  subtotal: number;
  discount: number;
  delivery_fee_customer: number;
  delivery_cost_business: number;
  pedidos_ya_commission: number;
  commission_rate_used?: number;
  packaging_cost: number;
  total: number;
  profit: number;
  payment_method: string;
  sales_channel: string;
  status: string;
  receiving_account_id?: string;
  created_at?: string;
}

export interface SaleItemRow {
  id?: string;
  sale_id: string;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  notes: string;
  subtotal: number;
  size_label?: string;
}

export const salesService = {
  getAll: async (from?: string, to?: string): Promise<SaleRow[]> => {
    let query = supabase
      .from('sales')
      .select('*')
      .order('sale_date', { ascending: false });
    if (from) query = query.gte('sale_date', from);
    if (to) query = query.lte('sale_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as SaleRow[];
  },

  getItems: async (saleId: string): Promise<SaleItemRow[]> => {
    const { data, error } = await supabase
      .from('sale_items')
      .select('*')
      .eq('sale_id', saleId);
    if (error) throw error;
    return (data || []) as SaleItemRow[];
  },

  create: async (sale: Omit<SaleRow, 'id' | 'created_at'>, items: Omit<SaleItemRow, 'id' | 'sale_id'>[]): Promise<SaleRow> => {
    const id = `sale-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('sales')
      .insert([{ ...sale, id }])
      .select()
      .single();
    if (error) throw error;

    if (items.length > 0) {
      const itemRows = items.map((item) => ({
        ...item,
        sale_id: id,
      }));
      await supabase.from('sale_items').insert(itemRows);
    }

    return data as SaleRow;
  },

  /**
   * Edit a completed sale atomically:
   * STEP 1: Reverse all effects of the original sale (inventory, account, commission)
   * STEP 2: Apply the new sale effects
   * STEP 3: Save the edited sale and create an audit record
   */
  edit: async (
    saleId: string,
    newSaleData: Partial<SaleRow>,
    newItems: Omit<SaleItemRow, 'id' | 'sale_id'>[],
    reason?: string
  ): Promise<void> => {
    // 1. Fetch original sale
    const { data: originalSale, error: fetchError } = await supabase
      .from('sales')
      .select('*')
      .eq('id', saleId)
      .single();
    if (fetchError) throw fetchError;
    if (!originalSale) throw new Error('Venta no encontrada');
    if (originalSale.status === 'cancelled') throw new Error('No se puede editar una venta cancelada');

    // 2. Fetch original sale items
    const { data: originalItems } = await supabase
      .from('sale_items')
      .select('*')
      .eq('sale_id', saleId);
    const origItems = (originalItems || []) as SaleItemRow[];

    // ── STEP 1: REVERSE ORIGINAL EFFECTS ──────────────────────────────────

    // 1a. Reverse financial transaction (account balance)
    if (originalSale.receiving_account_id) {
      await supabase
        .from('financial_transactions')
        .delete()
        .eq('reference_id', saleId)
        .eq('transaction_type', 'sale');
    }

    // 1b. Return inventory consumed by original sale items
    for (const item of origItems) {
      // Return recipe ingredients
      let recipeQuery = supabase.from('product_size_recipes').select('*').eq('product_id', item.product_id);
      if (item.size_label) recipeQuery = recipeQuery.eq('size_label', item.size_label);
      const { data: recipeRows } = await recipeQuery;
      const recipes = (recipeRows || []) as Array<{ ingredient_id: string; dose: number }>;
      for (const ri of recipes) {
        const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', ri.ingredient_id).single();
        if (ing) {
          const returnAmt = Number(ri.dose) * Number(item.quantity);
          const newStock = Number(ing.stock) + returnAmt;
          let newStatus = 'ok';
          if (newStock <= 0) newStatus = 'out';
          else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
          else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
          await supabase.from('ingredients').update({ stock: newStock, status: newStatus }).eq('id', ri.ingredient_id);
        }
      }

      // Return empaque ingredient consumption
      if ((item as Record<string, unknown>).empaque_lines) {
        try {
          const empaqueLines = typeof (item as Record<string, unknown>).empaque_lines === 'string'
            ? JSON.parse((item as Record<string, unknown>).empaque_lines as string)
            : (item as Record<string, unknown>).empaque_lines;
          if (Array.isArray(empaqueLines)) {
            for (const empLine of empaqueLines) {
              if (!empLine.recipe) continue;
              for (const matLine of empLine.recipe) {
                const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', matLine.materialId).single();
                if (ing) {
                  const returnAmt = Number(matLine.quantity) * Number(empLine.quantity) * Number(item.quantity);
                  const newStock = Number(ing.stock) + returnAmt;
                  let newStatus = 'ok';
                  if (newStock <= 0) newStatus = 'out';
                  else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
                  else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
                  await supabase.from('ingredients').update({ stock: newStock, status: newStatus }).eq('id', matLine.materialId);
                }
              }
            }
          }
        } catch { /* ignore */ }
      }

      // Return extra ingredient consumption
      if ((item as Record<string, unknown>).extra_lines) {
        try {
          const extraLines = typeof (item as Record<string, unknown>).extra_lines === 'string'
            ? JSON.parse((item as Record<string, unknown>).extra_lines as string)
            : (item as Record<string, unknown>).extra_lines;
          if (Array.isArray(extraLines)) {
            for (const extraLine of extraLines) {
              if (!extraLine.recipe) continue;
              for (const ri of extraLine.recipe) {
                const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', ri.ingredient_id).single();
                if (ing) {
                  const returnAmt = Number(ri.dose) * Number(extraLine.quantity || 1) * Number(item.quantity);
                  const newStock = Number(ing.stock) + returnAmt;
                  let newStatus = 'ok';
                  if (newStock <= 0) newStatus = 'out';
                  else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
                  else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
                  await supabase.from('ingredients').update({ stock: newStock, status: newStatus }).eq('id', ri.ingredient_id);
                }
              }
            }
          }
        } catch { /* ignore */ }
      }
    }

    // ── STEP 2: APPLY NEW EFFECTS ──────────────────────────────────────────

    const mergedSale = { ...originalSale, ...newSaleData };

    // 2a. Consume inventory for new items
    for (const item of newItems) {
      let recipeQuery = supabase.from('product_size_recipes').select('*').eq('product_id', item.product_id);
      if (item.size_label) recipeQuery = recipeQuery.eq('size_label', item.size_label);
      const { data: recipeRows } = await recipeQuery;
      const recipes = (recipeRows || []) as Array<{ ingredient_id: string; dose: number }>;
      for (const ri of recipes) {
        const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', ri.ingredient_id).single();
        if (ing) {
          const consumeAmt = Number(ri.dose) * Number(item.quantity);
          const newStock = Math.max(0, Number(ing.stock) - consumeAmt);
          let newStatus = 'ok';
          if (newStock <= 0) newStatus = 'out';
          else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
          else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
          await supabase.from('ingredients').update({ stock: newStock, status: newStatus }).eq('id', ri.ingredient_id);
        }
      }

      // Consume empaque ingredients for new items
      if ((item as Record<string, unknown>).empaque_lines) {
        try {
          const empaqueLines = typeof (item as Record<string, unknown>).empaque_lines === 'string'
            ? JSON.parse((item as Record<string, unknown>).empaque_lines as string)
            : (item as Record<string, unknown>).empaque_lines;
          if (Array.isArray(empaqueLines)) {
            for (const empLine of empaqueLines) {
              if (!empLine.recipe) continue;
              for (const matLine of empLine.recipe) {
                const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', matLine.materialId).single();
                if (ing) {
                  const consumeAmt = Number(matLine.quantity) * Number(empLine.quantity) * Number(item.quantity);
                  const newStock = Math.max(0, Number(ing.stock) - consumeAmt);
                  let newStatus = 'ok';
                  if (newStock <= 0) newStatus = 'out';
                  else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
                  else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
                  await supabase.from('ingredients').update({ stock: newStock, status: newStatus }).eq('id', matLine.materialId);
                }
              }
            }
          }
        } catch { /* ignore */ }
      }
    }

    // 2b. Create new financial transaction for new receiving account
    const newAccountId = newSaleData.receiving_account_id ?? originalSale.receiving_account_id;
    let newTotal = newSaleData.total ?? originalSale.total;
    if (newAccountId) {
      const newTxId = `ftx-sale-edit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      await supabase.from('financial_transactions').insert([{
        id: newTxId,
        account_id: newAccountId,
        transaction_date: newSaleData.sale_date ?? originalSale.sale_date,
        description: `Venta editada — ${originalSale.receipt_number}`,
        transaction_type: 'sale',
        amount_in: Number(newTotal),
        amount_out: 0,
        reference_id: saleId,
        reference_type: 'sale',
        notes: reason || 'Venta editada',
      }]);
    }

    // ── STEP 3: SAVE EDITED SALE ───────────────────────────────────────────

    // 3a. Replace sale items
    await supabase.from('sale_items').delete().eq('sale_id', saleId);
    if (newItems.length > 0) {
      const itemRows = newItems.map((item) => ({ ...item, sale_id: saleId }));
      await supabase.from('sale_items').insert(itemRows);
    }

    // 3b. Update sale record
    const { error: updateError } = await supabase
      .from('sales')
      .update({ ...newSaleData })
      .eq('id', saleId);
    if (updateError) throw updateError;

    // 3c. Create audit record
    const auditId = `seh-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await supabase.from('sale_edit_history').insert([{
      id: auditId,
      sale_id: saleId,
      edited_at: new Date().toISOString(),
      previous_total: Number(originalSale.total),
      new_total: Number(newTotal),
      previous_sales_channel: originalSale.sales_channel,
      new_sales_channel: newSaleData.sales_channel ?? originalSale.sales_channel,
      previous_payment_method: originalSale.payment_method,
      new_payment_method: newSaleData.payment_method ?? originalSale.payment_method,
      previous_receiving_account_id: originalSale.receiving_account_id,
      new_receiving_account_id: newAccountId,
      previous_pedidos_ya_commission: Number(originalSale.pedidos_ya_commission),
      new_pedidos_ya_commission: Number(newSaleData.pedidos_ya_commission ?? originalSale.pedidos_ya_commission),
      reason: reason || 'Edición manual',
      notes: `Venta ${originalSale.receipt_number} editada`,
    }]);
  },

  /**
   * Cancel a completed sale and reverse ALL its financial and inventory effects.
   * - Marks sale as 'cancelled'
   * - Reverses the financial transaction (account balance)
   * - Returns consumed inventory ingredients
   * - Creates an audit record in sale_cancellations
   * - The sale remains in the DB for audit purposes but is excluded from all financial totals
   */
  cancel: async (saleId: string, reason?: string): Promise<void> => {
    // 1. Fetch the sale
    const { data: sale, error: fetchError } = await supabase
      .from('sales')
      .select('*')
      .eq('id', saleId)
      .single();
    if (fetchError) throw fetchError;
    if (!sale) throw new Error('Venta no encontrada');
    if (sale.status === 'cancelled') throw new Error('Esta venta ya fue cancelada');

    // 2. Fetch sale items for inventory reversal
    const { data: items } = await supabase
      .from('sale_items')
      .select('*')
      .eq('sale_id', saleId);
    const saleItems = (items || []) as SaleItemRow[];

    // 3. Mark sale as cancelled
    const { error: cancelError } = await supabase
      .from('sales')
      .update({ status: 'cancelled' })
      .eq('id', saleId);
    if (cancelError) throw cancelError;

    // 4. Reverse the financial transaction for this sale
    await supabase
      .from('financial_transactions')
      .delete()
      .eq('reference_id', saleId)
      .eq('transaction_type', 'sale');

    // 5. Return inventory for each sale item
    for (const item of saleItems) {
      // Get the product to find its recipe
      const { data: product } = await supabase
        .from('products')
        .select('product_type')
        .eq('id', item.product_id)
        .single();

      if (!product) continue;

      // Get recipe ingredients for this product/size
      let recipeQuery = supabase.from('product_size_recipes').select('*').eq('product_id', item.product_id);
      if (item.size_label) {
        recipeQuery = recipeQuery.eq('size_label', item.size_label);
      }
      const { data: recipeRows } = await recipeQuery;
      const recipes = (recipeRows || []) as Array<{ ingredient_id: string; dose: number; size_label: string }>;

      for (const ri of recipes) {
        const { data: ing } = await supabase
          .from('ingredients')
          .select('stock, min_stock')
          .eq('id', ri.ingredient_id)
          .single();
        if (ing) {
          const returnAmount = Number(ri.dose) * Number(item.quantity);
          const newStock = Number(ing.stock) + returnAmount;
          let newStatus = 'ok';
          if (newStock <= 0) newStatus = 'out';
          else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
          else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
          await supabase
            .from('ingredients')
            .update({ stock: newStock, status: newStatus })
            .eq('id', ri.ingredient_id);
        }
      }

      // Also parse empaque_lines if stored as JSON to return packaging materials
      if ((item as Record<string, unknown>).empaque_lines) {
        try {
          const empaqueLines = typeof (item as Record<string, unknown>).empaque_lines === 'string'
            ? JSON.parse((item as Record<string, unknown>).empaque_lines as string)
            : (item as Record<string, unknown>).empaque_lines;
          if (Array.isArray(empaqueLines)) {
            for (const empLine of empaqueLines) {
              if (!empLine.recipe) continue;
              for (const matLine of empLine.recipe) {
                const { data: ing } = await supabase
                  .from('ingredients')
                  .select('stock, min_stock')
                  .eq('id', matLine.materialId)
                  .single();
                if (ing) {
                  const returnAmount = Number(matLine.quantity) * Number(empLine.quantity) * Number(item.quantity);
                  const newStock = Number(ing.stock) + returnAmount;
                  let newStatus = 'ok';
                  if (newStock <= 0) newStatus = 'out';
                  else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
                  else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
                  await supabase
                    .from('ingredients')
                    .update({ stock: newStock, status: newStatus })
                    .eq('id', matLine.materialId);
                }
              }
            }
          }
        } catch {
          // ignore JSON parse errors
        }
      }
    }

    // 6. Create audit record
    const cancellationId = `canc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await supabase.from('sale_cancellations').insert([{
      id: cancellationId,
      sale_id: saleId,
      cancelled_at: new Date().toISOString(),
      reason: reason || 'Cancelación manual',
      original_total: Number(sale.total),
      original_profit: Number(sale.profit),
      original_payment_method: sale.payment_method,
      original_receiving_account_id: sale.receiving_account_id,
      original_sales_channel: sale.sales_channel,
      inventory_reversed: true,
      financial_reversed: true,
      notes: `Venta ${sale.receipt_number} cancelada`,
    }]);
  },

  getNextReceiptNumber: async (): Promise<string> => {
    const { count } = await supabase
      .from('sales')
      .select('*', { count: 'exact', head: true });
    const num = (count || 0) + 245;
    return `REC-0${num}`;
  },
};

// ─── Gastos Operativos ────────────────────────────────────────────────────────

export const gastosService = {
  getAll: async (from?: string, to?: string): Promise<GastoOperativo[]> => {
    let query = supabase
      .from('operational_expenses')
      .select('*')
      .eq('expense_status', 'active')
      .order('expense_date', { ascending: false });
    if (from) query = query.gte('expense_date', from);
    if (to) query = query.lte('expense_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as GastoOperativo[];
  },

  create: async (gasto: Omit<GastoOperativo, 'id' | 'created_at'>): Promise<GastoOperativo> => {
    const { data, error } = await supabase
      .from('operational_expenses')
      .insert([{ ...gasto, id: `gasto-${Date.now()}`, expense_status: 'active' }])
      .select()
      .single();
    if (error) throw error;
    return data as GastoOperativo;
  },

  update: async (id: string, gasto: Partial<GastoOperativo>): Promise<GastoOperativo> => {
    const { data, error } = await supabase
      .from('operational_expenses')
      .update(gasto)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as GastoOperativo;
  },

  /**
   * Delete an expense and reverse ALL its financial effects:
   * - Deletes linked financial transaction (if paid from business account)
   * - Deletes linked capital entry (if funded by Capital de inversión)
   * - Marks expense as cancelled (soft delete for audit trail)
   */
  delete: async (id: string): Promise<void> => {
    // 1. Soft-delete the expense (keep for audit)
    const { error: updateError } = await supabase
      .from('operational_expenses')
      .update({ expense_status: 'cancelled' })
      .eq('id', id);
    if (updateError) throw updateError;

    // 2. Delete linked financial transaction (business account payment)
    await supabase
      .from('financial_transactions')
      .delete()
      .eq('reference_id', id);

    // 3. Delete linked capital entry (Capital de inversión funding)
    // Capital entries for expenses follow the pattern: cap-exp-{expense_id}
    const capEntryId = `cap-exp-${id}`;
    await supabase
      .from('capital_entries')
      .delete()
      .eq('id', capEntryId);

    // Also delete any capital entries that reference this expense via source_record_id
    await supabase
      .from('capital_entries')
      .delete()
      .eq('source_record_id', id)
      .eq('source_record_type', 'operational_expense');
  },
};

// ─── Compras (Purchase Orders) ────────────────────────────────────────────────

export const comprasService = {
  getAll: async (): Promise<PurchaseOrder[]> => {
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('*, items:purchase_order_items(*)')
      .order('order_date', { ascending: false });
    if (error) throw error;
    return (data || []) as PurchaseOrder[];
  },

  create: async (order: Omit<PurchaseOrder, 'id' | 'created_at' | 'items'>, items: Omit<PurchaseOrderItem, 'id' | 'purchase_order_id'>[]): Promise<PurchaseOrder> => {
    const orderId = `po-${Date.now()}`;
    const poNumber = `PO-${String(Date.now()).slice(-6)}`;
    const { data: orderData, error: orderError } = await supabase
      .from('purchase_orders')
      .insert([{ ...order, id: orderId, po_number: poNumber, inventory_updated: false }])
      .select()
      .single();
    if (orderError) throw orderError;

    if (items.length > 0) {
      const itemsToInsert = items.map((item) => ({
        ...item,
        id: `poi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        purchase_order_id: orderId,
      }));
      const { error: itemsError } = await supabase
        .from('purchase_order_items')
        .insert(itemsToInsert);
      if (itemsError) throw itemsError;
    }

    return { ...orderData, items } as PurchaseOrder;
  },

  /**
   * Edit an existing purchase invoice.
   * - Updates supplier, date, notes, invoice_reference, is_historical, items, total
   * - If payment source changed: reverses old accounting transaction and creates new one
   * - If items changed on a received non-historical purchase: adjusts inventory delta
   * - Never duplicates inventory for historical purchases
   * - Records audit history entry for payment source changes
   */
  editPurchase: async (
    orderId: string,
    updates: {
      supplier?: string;
      order_date?: string;
      notes?: string;
      invoice_reference?: string;
      is_historical?: boolean;
      funding_source?: 'business_account' | 'external_family' | 'owner_personal';
      payment_account_id?: string;
      items?: Omit<PurchaseOrderItem, 'id' | 'purchase_order_id'>[];
    }
  ): Promise<void> => {
    // 1. Fetch current order
    const { data: order, error: fetchError } = await supabase
      .from('purchase_orders')
      .select('*, items:purchase_order_items(*)')
      .eq('id', orderId)
      .single();
    if (fetchError) throw fetchError;
    if (!order) throw new Error('Compra no encontrada');

    const oldFunding = order.funding_source || 'business_account';
    const oldAccountId = order.payment_account_id;
    const oldTotal = Number(order.total);
    const wasReceived = order.status === 'received';
    const wasHistorical = !!order.is_historical;

    const newFunding = updates.funding_source !== undefined ? updates.funding_source : oldFunding;
    const newAccountId = updates.payment_account_id !== undefined ? updates.payment_account_id : oldAccountId;
    const newIsHistorical = updates.is_historical !== undefined ? updates.is_historical : wasHistorical;

    // 2. Compute new total from items if provided
    let newTotal = oldTotal;
    if (updates.items && updates.items.length > 0) {
      newTotal = updates.items.reduce((s, i) => s + (i.quantity_ordered * i.unit_price), 0);
    }

    const fundingChanged = newFunding !== oldFunding || newAccountId !== oldAccountId;
    const totalChanged = Math.abs(newTotal - oldTotal) > 0.001;

    // 3. If received and items changed and NOT historical: adjust inventory delta
    if (wasReceived && !wasHistorical && updates.items && updates.items.length > 0) {
      const oldItems: PurchaseOrderItem[] = order.items || [];
      // Build map of old quantities
      const oldQtyMap: Record<string, { qty: number; type: string }> = {};
      for (const oi of oldItems) {
        oldQtyMap[oi.ingredient_id] = { qty: Number(oi.quantity_received || oi.quantity_ordered), type: oi.item_type };
      }
      // Apply delta for each new item
      for (const ni of updates.items) {
        const oldEntry = oldQtyMap[ni.ingredient_id];
        const oldQty = oldEntry ? oldEntry.qty : 0;
        const newQty = Number(ni.quantity_ordered);
        const delta = newQty - oldQty;
        if (delta === 0) continue;
        if (ni.item_type === 'packaging_material') {
          const { data: mat } = await supabase.from('packaging_materials').select('stock').eq('id', ni.ingredient_id).single();
          if (mat) {
            await supabase.from('packaging_materials').update({ stock: Math.max(0, Number(mat.stock) + delta) }).eq('id', ni.ingredient_id);
          }
        } else {
          const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', ni.ingredient_id).single();
          if (ing) {
            const newStock = Math.max(0, Number(ing.stock) + delta);
            let newStatus = 'ok';
            if (newStock <= 0) newStatus = 'out';
            else if (newStock <= (ing.min_stock || 0) * 0.5) newStatus = 'critical';
            else if (newStock <= (ing.min_stock || 0)) newStatus = 'low';
            await supabase.from('ingredients').update({ stock: newStock, status: newStatus, last_updated: new Date().toISOString().split('T')[0] }).eq('id', ni.ingredient_id);
          }
        }
      }
      // Remove inventory for items that were removed
      for (const oi of oldItems) {
        const stillExists = updates.items!.find((ni) => ni.ingredient_id === oi.ingredient_id);
        if (!stillExists) {
          const oldQty = Number(oi.quantity_received || oi.quantity_ordered);
          if (oi.item_type === 'packaging_material') {
            const { data: mat } = await supabase.from('packaging_materials').select('stock').eq('id', oi.ingredient_id).single();
            if (mat) {
              await supabase.from('packaging_materials').update({ stock: Math.max(0, Number(mat.stock) - oldQty) }).eq('id', oi.ingredient_id);
            }
          } else {
            const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', oi.ingredient_id).single();
            if (ing) {
              const newStock = Math.max(0, Number(ing.stock) - oldQty);
              let newStatus = 'ok';
              if (newStock <= 0) newStatus = 'out';
              else if (newStock <= (ing.min_stock || 0) * 0.5) newStatus = 'critical';
              else if (newStock <= (ing.min_stock || 0)) newStatus = 'low';
              await supabase.from('ingredients').update({ stock: newStock, status: newStatus, last_updated: new Date().toISOString().split('T')[0] }).eq('id', oi.ingredient_id);
            }
          }
        }
      }
    }

    // 4. Handle accounting transaction reversal / update (only for received purchases)
    if (wasReceived && (fundingChanged || totalChanged)) {
      const isOldExternal = oldFunding === 'external_family' || oldFunding === 'owner_personal';
      const isNewExternal = newFunding === 'external_family' || newFunding === 'owner_personal';

      // Reverse old capital entry if old was external
      if (isOldExternal) {
        const capEntryId = `cap-mig-po-${orderId}`;
        await supabase.from('capital_entries').delete().eq('id', capEntryId);
      }

      // Reverse old financial transaction if old was business account
      if (!isOldExternal && oldAccountId) {
        // Delete old transaction for this purchase
        await supabase
          .from('financial_transactions')
          .delete()
          .eq('reference_id', orderId)
          .eq('transaction_type', 'inventory_purchase');

        // Restore old account balance by creating a reversal credit
        // (We just delete the debit transaction — the balance recalculates from transactions)
      }

      // Create new accounting entry based on new funding source
      if (isNewExternal) {
        // New: Capital de inversión — create capital entry with source link
        const capEntryId = `cap-mig-po-${orderId}`;
        await supabase.from('capital_entries').upsert([{
          id: capEntryId,
          entry_date: updates.order_date || order.order_date,
          entry_type: 'contribution',
          amount: newTotal,
          description: `Capital de inversión — Compra: ${updates.supplier || order.supplier} (${order.po_number})`,
          notes: `Compra financiada con Capital de inversión. ID: ${orderId}`,
          entry_source: 'external_family',
          source_record_id: orderId,
          source_record_type: 'purchase_order',
        }], { onConflict: 'id', ignoreDuplicates: false });
      } else if (newAccountId) {
        // New: business account — create financial transaction
        await supabase.from('financial_transactions').insert([{
          id: `ftx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          account_id: newAccountId,
          transaction_date: updates.order_date || order.order_date,
          description: newIsHistorical
            ? `Compra histórica de inventario — ${updates.supplier || order.supplier} (${order.po_number})`
            : `Compra de inventario — ${updates.supplier || order.supplier} (${order.po_number})`,
          transaction_type: 'inventory_purchase',
          amount_in: 0,
          amount_out: newTotal,
          reference_id: orderId,
          reference_type: 'purchase_order',
          notes: `Editado. Proveedor: ${updates.supplier || order.supplier}`,
        }]);
      }
    } else if (wasReceived && !fundingChanged && !isExternalFundingSource(newFunding) && (totalChanged || (updates.order_date && updates.order_date !== order.order_date))) {
      // Same funding source but total or date changed — update existing transaction
      const updateFields: Record<string, unknown> = {};
      if (totalChanged) updateFields.amount_out = newTotal;
      if (updates.order_date && updates.order_date !== order.order_date) updateFields.transaction_date = updates.order_date;
      if (Object.keys(updateFields).length > 0) {
        await supabase
          .from('financial_transactions')
          .update(updateFields)
          .eq('reference_id', orderId)
          .eq('transaction_type', 'inventory_purchase');
      }
    }

    // 5. Record audit history for payment source change
    if (wasReceived && fundingChanged) {
      const oldLabel = getFundingLabel(oldFunding, oldAccountId, order);
      const newLabel = getFundingLabel(newFunding, newAccountId, null);
      await supabase.from('purchase_edit_history').insert([{
        id: `peh-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        purchase_order_id: orderId,
        edited_at: new Date().toISOString(),
        field_changed: 'payment_source',
        old_value: oldLabel,
        new_value: newLabel,
        notes: `Fuente de pago cambiada de "${oldLabel}" a "${newLabel}"`,
      }]);
    }

    // 6. Update purchase_order_items if provided
    if (updates.items && updates.items.length > 0) {
      await supabase.from('purchase_order_items').delete().eq('purchase_order_id', orderId);
      const itemsToInsert = updates.items.map((item) => ({
        ...item,
        id: `poi-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        purchase_order_id: orderId,
        quantity_received: item.quantity_ordered,
        subtotal: item.quantity_ordered * item.unit_price,
      }));
      await supabase.from('purchase_order_items').insert(itemsToInsert);
    }

    // 7. Update the purchase_order record
    const orderUpdate: Record<string, unknown> = {};
    if (updates.supplier !== undefined) orderUpdate.supplier = updates.supplier;
    if (updates.order_date !== undefined) orderUpdate.order_date = updates.order_date;
    if (updates.notes !== undefined) orderUpdate.notes = updates.notes;
    if (updates.invoice_reference !== undefined) orderUpdate.invoice_reference = updates.invoice_reference;
    if (updates.is_historical !== undefined) orderUpdate.is_historical = updates.is_historical;
    if (updates.funding_source !== undefined) orderUpdate.funding_source = updates.funding_source;
    if (updates.payment_account_id !== undefined) orderUpdate.payment_account_id = updates.payment_account_id;
    if (updates.items && updates.items.length > 0) {
      orderUpdate.subtotal = newTotal;
      orderUpdate.total = newTotal;
    }

    const { error: updateError } = await supabase
      .from('purchase_orders')
      .update(orderUpdate)
      .eq('id', orderId);
    if (updateError) throw updateError;
  },

  receivePurchase: async (orderId: string, paymentAccountId?: string): Promise<void> => {
    const { data: order, error: fetchError } = await supabase
      .from('purchase_orders')
      .select('*, items:purchase_order_items(*)')
      .eq('id', orderId)
      .single();
    if (fetchError) throw fetchError;
    if (!order) throw new Error('Compra no encontrada');
    if (order.inventory_updated) throw new Error('Esta compra ya fue recibida y el inventario ya fue actualizado.');

    const items: PurchaseOrderItem[] = order.items || [];

    // Historical purchases: skip inventory quantity update entirely
    if (!order.is_historical) {
      for (const item of items) {
        const qty = item.quantity_received > 0 ? item.quantity_received : item.quantity_ordered;
        if (item.item_type === 'packaging_material') {
          const { data: mat, error: matFetchError } = await supabase
            .from('packaging_materials')
            .select('stock')
            .eq('id', item.ingredient_id)
            .single();
          if (!matFetchError && mat) {
            const newStock = (mat.stock || 0) + qty;
            await supabase
              .from('packaging_materials')
              .update({ stock: newStock })
              .eq('id', item.ingredient_id);
          }
        } else {
          const { data: ing, error: ingFetchError } = await supabase
            .from('ingredients')
            .select('stock, min_stock')
            .eq('id', item.ingredient_id)
            .single();
          if (!ingFetchError && ing) {
            const newStock = (ing.stock || 0) + qty;
            let newStatus = 'ok';
            if (newStock <= 0) newStatus = 'out';
            else if (newStock <= (ing.min_stock || 0) * 0.5) newStatus = 'critical';
            else if (newStock <= (ing.min_stock || 0)) newStatus = 'low';
            await supabase
              .from('ingredients')
              .update({ stock: newStock, status: newStatus, last_updated: new Date().toISOString().split('T')[0] })
              .eq('id', item.ingredient_id);
          }
        }
      }
    }

    const updatePayload: Record<string, unknown> = {
      status: 'received',
      received_at: new Date().toISOString().split('T')[0],
      inventory_updated: true,
    };
    if (paymentAccountId) {
      updatePayload.payment_account_id = paymentAccountId;
    }

    const { error: updateError } = await supabase
      .from('purchase_orders')
      .update(updatePayload)
      .eq('id', orderId);
    if (updateError) throw updateError;

    // Create capital entry if funded by Capital de inversión (with source link for reversibility)
    const effectiveFunding = order.funding_source || 'business_account';
    if (isExternalFundingSource(effectiveFunding)) {
      const capEntryId = `cap-mig-po-${orderId}`;
      const { data: existingCap } = await supabase
        .from('capital_entries')
        .select('id')
        .eq('id', capEntryId)
        .maybeSingle();
      if (!existingCap) {
        await supabase.from('capital_entries').insert([{
          id: capEntryId,
          entry_date: order.order_date,
          entry_type: 'contribution',
          amount: Number(order.total),
          description: `Capital de inversión — Compra: ${order.supplier} (${order.po_number})`,
          notes: `Compra financiada con Capital de inversión. ID: ${orderId}`,
          entry_source: 'external_family',
          source_record_id: orderId,
          source_record_type: 'purchase_order',
        }]);
      }
    }
  },

  /**
   * Delete a purchase and reverse its capital entry if funded by Capital de inversión.
   */
  delete: async (id: string): Promise<void> => {
    // Reverse capital entry if this purchase was funded by Capital de inversión
    const capEntryId = `cap-mig-po-${id}`;
    await supabase.from('capital_entries').delete().eq('id', capEntryId);
    // Also delete any capital entries linked via source_record_id
    await supabase
      .from('capital_entries')
      .delete()
      .eq('source_record_id', id)
      .eq('source_record_type', 'purchase_order');
    // Delete financial transactions
    await supabase.from('financial_transactions').delete().eq('reference_id', id);
    // Delete items and order
    await supabase.from('purchase_order_items').delete().eq('purchase_order_id', id);
    const { error } = await supabase.from('purchase_orders').delete().eq('id', id);
    if (error) throw error;
  },

  /**
   * Cancel a received purchase and reverse ALL its effects:
   * - Reverses inventory quantities (returns stock)
   * - Reverses financial transaction (restores account balance)
   * - Reverses capital entry (if funded by Capital de inversión)
   * - Marks purchase as cancelled (soft delete for audit)
   */
  cancelReceived: async (id: string, reason?: string): Promise<void> => {
    // 1. Fetch the purchase with items
    const { data: order, error: fetchError } = await supabase
      .from('purchase_orders')
      .select('*, items:purchase_order_items(*)')
      .eq('id', id)
      .single();
    if (fetchError) throw fetchError;
    if (!order) throw new Error('Compra no encontrada');
    if (order.status === 'cancelled') throw new Error('Esta compra ya fue cancelada');

    const items: PurchaseOrderItem[] = order.items || [];
    const wasHistorical = !!order.is_historical;

    // 2. Reverse inventory (only for non-historical received purchases)
    if (order.status === 'received' && !wasHistorical) {
      for (const item of items) {
        const qty = Number(item.quantity_received || item.quantity_ordered);
        if (item.item_type === 'packaging_material') {
          const { data: mat } = await supabase.from('packaging_materials').select('stock').eq('id', item.ingredient_id).single();
          if (mat) {
            await supabase.from('packaging_materials').update({ stock: Math.max(0, Number(mat.stock) - qty) }).eq('id', item.ingredient_id);
          }
        } else {
          const { data: ing } = await supabase.from('ingredients').select('stock, min_stock').eq('id', item.ingredient_id).single();
          if (ing) {
            const newStock = Math.max(0, Number(ing.stock) - qty);
            let newStatus = 'ok';
            if (newStock <= 0) newStatus = 'out';
            else if (newStock <= Number(ing.min_stock) * 0.5) newStatus = 'critical';
            else if (newStock <= Number(ing.min_stock)) newStatus = 'low';
            await supabase.from('ingredients').update({ stock: newStock, status: newStatus, last_updated: new Date().toISOString().split('T')[0] }).eq('id', item.ingredient_id);
          }
        }
      }
    }

    // 3. Reverse financial transaction (restores account balance)
    await supabase.from('financial_transactions').delete().eq('reference_id', id).eq('transaction_type', 'inventory_purchase');

    // 4. Reverse capital entry if funded by Capital de inversión
    const capEntryId = `cap-mig-po-${id}`;
    await supabase.from('capital_entries').delete().eq('id', capEntryId);
    await supabase.from('capital_entries').delete().eq('source_record_id', id).eq('source_record_type', 'purchase_order');

    // 5. Mark purchase as cancelled (soft delete)
    const { error: cancelError } = await supabase
      .from('purchase_orders')
      .update({ status: 'cancelled', purchase_status: 'cancelled' })
      .eq('id', id);
    if (cancelError) throw cancelError;
  },
};

// Helper: check if a funding source is external (no cash movement)
function isExternalFundingSource(funding: string): boolean {
  return funding === 'external_family' || funding === 'owner_personal';
}

// Helper: get human-readable label for funding source
function getFundingLabel(funding: string, accountId: string | undefined | null, order: Record<string, unknown> | null): string {
  if (funding === 'external_family' || funding === 'owner_personal') return 'Capital de inversión';
  if (accountId) return `Cuenta: ${accountId}`;
  return 'Cuenta Bunori';
}

// ─── Extras (Supabase) ────────────────────────────────────────────────────────

export interface ExtraRow {
  id: string;
  name: string;
  extra_type: 'recipe_extra' | 'charged_extra' | 'replacement';
  customer_charge: number;
  show_on_receipt: boolean;
  active: boolean;
  created_at?: string;
}

export interface ExtraRecipeIngredientRow {
  id: string;
  extra_id: string;
  ingredient_id: string;
  ingredient_name: string;
  dose: number;
  unit: string;
  unit_cost: number;
  dose_cost: number;
}

export interface ExtraWithRecipe extends ExtraRow {
  recipe: ExtraRecipeIngredientRow[];
  totalCost: number;
}

export const extrasService = {
  getAll: async (): Promise<ExtraWithRecipe[]> => {
    const { data: extras, error } = await supabase
      .from('extras')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;

    const { data: recipeRows } = await supabase
      .from('extra_recipe_ingredients')
      .select('*');

    const recipeMap: Record<string, ExtraRecipeIngredientRow[]> = {};
    (recipeRows || []).forEach((r: ExtraRecipeIngredientRow) => {
      if (!recipeMap[r.extra_id]) recipeMap[r.extra_id] = [];
      recipeMap[r.extra_id].push(r);
    });

    return (extras || []).map((e: ExtraRow) => {
      const recipe = recipeMap[e.id] || [];
      return {
        ...e,
        recipe,
        totalCost: recipe.reduce((s, r) => s + Number(r.dose_cost), 0),
      };
    });
  },

  create: async (
    extra: Omit<ExtraRow, 'id' | 'created_at'>,
    recipe: Omit<ExtraRecipeIngredientRow, 'id' | 'extra_id'>[]
  ): Promise<ExtraWithRecipe> => {
    const id = `extra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('extras')
      .insert([{ ...extra, id }])
      .select()
      .single();
    if (error) throw error;

    let savedRecipe: ExtraRecipeIngredientRow[] = [];
    if (recipe.length > 0) {
      const rows = recipe.map((r) => ({
        ...r,
        id: `eri-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        extra_id: id,
      }));
      const { data: recipeData } = await supabase
        .from('extra_recipe_ingredients')
        .insert(rows)
        .select();
      savedRecipe = (recipeData || []) as ExtraRecipeIngredientRow[];
    }

    return {
      ...(data as ExtraRow),
      recipe: savedRecipe,
      totalCost: savedRecipe.reduce((s, r) => s + Number(r.dose_cost), 0),
    };
  },

  update: async (
    id: string,
    extra: Partial<ExtraRow>,
    recipe: Omit<ExtraRecipeIngredientRow, 'id' | 'extra_id'>[]
  ): Promise<ExtraWithRecipe> => {
    const { data, error } = await supabase
      .from('extras')
      .update(extra)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    await supabase.from('extra_recipe_ingredients').delete().eq('extra_id', id);

    let savedRecipe: ExtraRecipeIngredientRow[] = [];
    if (recipe.length > 0) {
      const rows = recipe.map((r) => ({
        ...r,
        id: `eri-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        extra_id: id,
      }));
      const { data: recipeData } = await supabase
        .from('extra_recipe_ingredients')
        .insert(rows)
        .select();
      savedRecipe = (recipeData || []) as ExtraRecipeIngredientRow[];
    }

    return {
      ...(data as ExtraRow),
      recipe: savedRecipe,
      totalCost: savedRecipe.reduce((s, r) => s + Number(r.dose_cost), 0),
    };
  },

  delete: async (id: string): Promise<void> => {
    await supabase.from('extra_recipe_ingredients').delete().eq('extra_id', id);
    const { error } = await supabase.from('extras').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Empaques (Independent Packaging Configurations) ─────────────────────────

export interface EmpaqueRow {
  id: string;
  name: string;
  description: string;
  customer_charge: number;
  show_on_receipt: boolean;
  active: boolean;
  created_at?: string;
}

export interface EmpaqueRecipeItemRow {
  id: string;
  empaque_id: string;
  // ingredient_id references the shared ingredients table (single inventory source)
  ingredient_id: string;
  // material_id kept for DB compat (same value as ingredient_id)
  material_id: string;
  material_name: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  item_cost: number;
}

export interface EmpaqueWithRecipe extends EmpaqueRow {
  recipe: EmpaqueRecipeItemRow[];
  recipeCost: number;
}

export const empaquesService = {
  getAll: async (): Promise<EmpaqueWithRecipe[]> => {
    const { data: empaques, error } = await supabase
      .from('empaques')
      .select('*')
      .eq('active', true)
      .order('name', { ascending: true });
    if (error) throw error;

    const { data: recipeRows } = await supabase
      .from('empaque_recipe_items')
      .select('*');

    // Fetch all ingredients to get live unit costs
    const { data: ingredients } = await supabase
      .from('ingredients')
      .select('id, name, unit, unit_cost');
    const ingMap: Record<string, { name: string; unit: string; unit_cost: number }> = {};
    (ingredients || []).forEach((ing: { id: string; name: string; unit: string; unit_cost: number }) => {
      ingMap[ing.id] = { name: ing.name, unit: ing.unit, unit_cost: Number(ing.unit_cost) };
    });

    const recipeMap: Record<string, EmpaqueRecipeItemRow[]> = {};
    (recipeRows || []).forEach((r: EmpaqueRecipeItemRow) => {
      if (!recipeMap[r.empaque_id]) recipeMap[r.empaque_id] = [];
      // Use ingredient_id if available, fall back to material_id
      const ingId = r.ingredient_id || r.material_id;
      const ing = ingMap[ingId];
      // Recalculate live cost from actual ingredient unit cost
      const liveUnitCost = ing ? ing.unit_cost : Number(r.unit_cost);
      const liveItemCost = Number(r.quantity) * liveUnitCost;
      recipeMap[r.empaque_id].push({
        ...r,
        ingredient_id: ingId,
        material_id: ingId,
        material_name: ing ? ing.name : r.material_name,
        unit: ing ? ing.unit : r.unit,
        unit_cost: liveUnitCost,
        item_cost: liveItemCost,
      });
    });

    return (empaques || []).map((e: EmpaqueRow) => {
      const recipe = recipeMap[e.id] || [];
      return {
        ...e,
        recipe,
        recipeCost: recipe.reduce((s, r) => s + Number(r.item_cost), 0),
      };
    });
  },

  create: async (
    empaque: Omit<EmpaqueRow, 'id' | 'created_at'>,
    recipe: Omit<EmpaqueRecipeItemRow, 'id' | 'empaque_id'>[]
  ): Promise<EmpaqueWithRecipe> => {
    const id = `emp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('empaques')
      .insert([{ ...empaque, id }])
      .select()
      .single();
    if (error) throw error;

    let savedRecipe: EmpaqueRecipeItemRow[] = [];
    if (recipe.length > 0) {
      const rows = recipe.map((r) => ({
        ...r,
        id: `eri-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        empaque_id: id,
        // Store ingredient_id = material_id so both columns are consistent
        ingredient_id: r.ingredient_id || r.material_id,
        material_id: r.ingredient_id || r.material_id,
      }));
      const { data: recipeData } = await supabase
        .from('empaque_recipe_items')
        .insert(rows)
        .select();
      savedRecipe = (recipeData || []) as EmpaqueRecipeItemRow[];
    }

    return {
      ...(data as EmpaqueRow),
      recipe: savedRecipe,
      recipeCost: savedRecipe.reduce((s, r) => s + Number(r.item_cost), 0),
    };
  },

  update: async (
    id: string,
    empaque: Partial<EmpaqueRow>,
    recipe: Omit<EmpaqueRecipeItemRow, 'id' | 'empaque_id'>[]
  ): Promise<EmpaqueWithRecipe> => {
    const { data, error } = await supabase
      .from('empaques')
      .update(empaque)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;

    await supabase.from('empaque_recipe_items').delete().eq('empaque_id', id);

    let savedRecipe: EmpaqueRecipeItemRow[] = [];
    if (recipe.length > 0) {
      const rows = recipe.map((r) => ({
        ...r,
        id: `eri-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        empaque_id: id,
        ingredient_id: r.ingredient_id || r.material_id,
        material_id: r.ingredient_id || r.material_id,
      }));
      const { data: recipeData } = await supabase
        .from('empaque_recipe_items')
        .insert(rows)
        .select();
      savedRecipe = (recipeData || []) as EmpaqueRecipeItemRow[];
    }

    return {
      ...(data as EmpaqueRow),
      recipe: savedRecipe,
      recipeCost: savedRecipe.reduce((s, r) => s + Number(r.item_cost), 0),
    };
  },

  delete: async (id: string): Promise<void> => {
    await supabase.from('empaque_recipe_items').delete().eq('empaque_id', id);
    const { error } = await supabase.from('empaques').delete().eq('id', id);
    if (error) throw error;
  },
};

// ─── Packaging Materials (Supabase) ──────────────────────────────────────────
// NOTE: packaging_materials is used only for purchase orders (Compras).
// Empaque recipes now reference the shared `ingredients` table directly.

export interface PackagingMaterialRow {
  id: string;
  name: string;
  unit: string;
  bag_content: number;
  bag_price: number;
  unit_cost: number;
  dose: number;
  dose_cost: number;
  stock: number;
  min_stock: number;
  provider: string;
  last_updated: string;
  status: string;
  created_at?: string;
}

export const packagingMaterialsService = {
  getAll: async (): Promise<PackagingMaterialRow[]> => {
    const { data, error } = await supabase
      .from('packaging_materials')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as PackagingMaterialRow[];
  },
};

// ─── Finance ──────────────────────────────────────────────────────────────────

// Categories that are genuine operating expenses (NOT inventory purchases)
const OPERATING_EXPENSE_CATEGORIES = [
  'Luz', 'Agua', 'Internet', 'Gas', 'Alquiler',
  'Publicidad', 'Transporte', 'Limpieza', 'Otros',
];

export const financeService = {
  getSalesData: async (from: string, to: string) => {
    const { data, error } = await supabase
      .from('sales')
      .select('*, items:sale_items(id, product_id, product_name, quantity, unit_price, subtotal, recipe_cost, extra_recipe_cost, charged_extras_total, sales_channel, size_label)')
      .gte('sale_date', from)
      .lte('sale_date', to)
      .eq('status', 'completed')
      .order('sale_date', { ascending: false });
    if (error) throw error;
    // Ensure pedidos_ya_commission is always a number (never null/undefined)
    return (data || []).map((sale: Record<string, unknown>) => ({
      ...sale,
      pedidos_ya_commission: Number(sale.pedidos_ya_commission) || 0,
      delivery_cost_business: Number(sale.delivery_cost_business) || 0,
    }));
  },

  getGastosData: async (from: string, to: string) => {
    // Only fetch genuine operating expenses — exclude any inventory/packaging purchase categories
    // Also exclude cancelled expenses (soft-deleted)
    const { data, error } = await supabase
      .from('operational_expenses')
      .select('*')
      .gte('expense_date', from)
      .lte('expense_date', to)
      .in('category', OPERATING_EXPENSE_CATEGORIES)
      .neq('expense_status', 'cancelled')
      .order('expense_date', { ascending: false });
    if (error) throw error;
    return data || [];
  },

  getPurchasesData: async (from: string, to: string) => {
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('*, items:purchase_order_items(*)')
      .gte('order_date', from)
      .lte('order_date', to)
      .order('order_date', { ascending: false });
    if (error) throw error;
    return data || [];
  },
};

// ─── Accounting System ────────────────────────────────────────────────────────

export interface MoneyAccount {
  id: string;
  name: string;
  account_type: 'cash' | 'bank' | 'digital' | 'other';
  opening_balance: number;
  actual_balance?: number;
  active: boolean;
  notes: string;
  created_at?: string;
}

export interface FinancialTransaction {
  id: string;
  account_id: string;
  transaction_date: string;
  description: string;
  transaction_type:
    | 'sale' |'inventory_purchase' |'operating_expense' |'transfer_in' |'transfer_out' |'capital_contribution' |'owner_withdrawal' |'manual_adjustment' |'refund';
  amount_in: number;
  amount_out: number;
  reference_id: string;
  reference_type: string;
  notes: string;
  created_at?: string;
}

export interface AccountReconciliation {
  id: string;
  account_id: string;
  reconciliation_date: string;
  expected_balance: number;
  actual_balance: number;
  difference: number;
  notes: string;
  created_at?: string;
}

export interface CapitalEntry {
  id: string;
  entry_date: string;
  entry_type: 'contribution' | 'withdrawal' | 'capital_return';
  amount: number;
  account_id?: string;
  description: string;
  notes: string;
  entry_source?: 'direct' | 'external_family' | 'bank_deposit' | 'target';
  capital_target?: number;
  created_at?: string;
}

export const accountingService = {
  // ── Accounts ──────────────────────────────────────────────────────────────

  getAccounts: async (): Promise<MoneyAccount[]> => {
    const { data, error } = await supabase
      .from('money_accounts')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as MoneyAccount[];
  },

  createAccount: async (account: Omit<MoneyAccount, 'id' | 'created_at'>): Promise<MoneyAccount> => {
    const id = `acc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('money_accounts')
      .insert([{ ...account, id }])
      .select()
      .single();
    if (error) throw error;
    return data as MoneyAccount;
  },

  updateAccount: async (id: string, account: Partial<MoneyAccount>): Promise<MoneyAccount> => {
    const { data, error } = await supabase
      .from('money_accounts')
      .update(account)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as MoneyAccount;
  },

  deleteAccount: async (id: string): Promise<void> => {
    const { error } = await supabase.from('money_accounts').delete().eq('id', id);
    if (error) throw error;
  },

  // ── Transactions ──────────────────────────────────────────────────────────

  getTransactions: async (accountId?: string, from?: string, to?: string): Promise<FinancialTransaction[]> => {
    let query = supabase
      .from('financial_transactions')
      .select('*')
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false });
    if (accountId) query = query.eq('account_id', accountId);
    if (from) query = query.gte('transaction_date', from);
    if (to) query = query.lte('transaction_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as FinancialTransaction[];
  },

  createTransaction: async (tx: Omit<FinancialTransaction, 'id' | 'created_at'>): Promise<FinancialTransaction> => {
    const id = `ftx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('financial_transactions')
      .insert([{ ...tx, id }])
      .select()
      .single();
    if (error) throw error;
    return data as FinancialTransaction;
  },

  deleteTransactionsByReference: async (referenceId: string): Promise<void> => {
    const { error } = await supabase
      .from('financial_transactions')
      .delete()
      .eq('reference_id', referenceId);
    if (error) throw error;
  },

  // Check if a transaction already exists for a reference (prevents duplicates)
  transactionExistsForReference: async (referenceId: string, txType: string): Promise<boolean> => {
    const { count } = await supabase
      .from('financial_transactions')
      .select('*', { count: 'exact', head: true })
      .eq('reference_id', referenceId)
      .eq('transaction_type', txType);
    return (count || 0) > 0;
  },

  // ── Transfers ─────────────────────────────────────────────────────────────

  createTransfer: async (
    fromAccountId: string,
    toAccountId: string,
    amount: number,
    date: string,
    description: string,
    notes: string
  ): Promise<void> => {
    const transferId = `transfer-${Date.now()}`;
    const outId = `ftx-out-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const inId = `ftx-in-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    const { error: outError } = await supabase.from('financial_transactions').insert([{
      id: outId,
      account_id: fromAccountId,
      transaction_date: date,
      description,
      transaction_type: 'transfer_out',
      amount_in: 0,
      amount_out: amount,
      reference_id: transferId,
      reference_type: 'transfer',
      notes,
    }]);
    if (outError) throw outError;

    const { error: inError } = await supabase.from('financial_transactions').insert([{
      id: inId,
      account_id: toAccountId,
      transaction_date: date,
      description,
      transaction_type: 'transfer_in',
      amount_in: amount,
      amount_out: 0,
      reference_id: transferId,
      reference_type: 'transfer',
      notes,
    }]);
    if (inError) throw inError;
  },

  // ── Expected Balance ──────────────────────────────────────────────────────

  getExpectedBalance: async (accountId: string): Promise<number> => {
    const { data: account } = await supabase
      .from('money_accounts')
      .select('opening_balance')
      .eq('id', accountId)
      .single();
    const openingBalance = Number(account?.opening_balance || 0);

    const { data: txs } = await supabase
      .from('financial_transactions')
      .select('amount_in, amount_out')
      .eq('account_id', accountId);

    const txBalance = (txs || []).reduce((s, tx) => s + Number(tx.amount_in) - Number(tx.amount_out), 0);
    return openingBalance + txBalance;
  },

  getAllExpectedBalances: async (): Promise<Record<string, number>> => {
    const { data: accounts } = await supabase.from('money_accounts').select('id, opening_balance');
    const { data: txs } = await supabase
      .from('financial_transactions')
      .select('account_id, amount_in, amount_out');

    const balances: Record<string, number> = {};
    (accounts || []).forEach((acc: { id: string; opening_balance: number }) => {
      balances[acc.id] = Number(acc.opening_balance);
    });
    (txs || []).forEach((tx: { account_id: string; amount_in: number; amount_out: number }) => {
      if (balances[tx.account_id] !== undefined) {
        balances[tx.account_id] += Number(tx.amount_in) - Number(tx.amount_out);
      }
    });
    return balances;
  },

  // ── Reconciliation ────────────────────────────────────────────────────────

  getReconciliations: async (accountId?: string): Promise<AccountReconciliation[]> => {
    let query = supabase
      .from('account_reconciliations')
      .select('*')
      .order('reconciliation_date', { ascending: false });
    if (accountId) query = query.eq('account_id', accountId);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as AccountReconciliation[];
  },

  createReconciliation: async (rec: Omit<AccountReconciliation, 'id' | 'created_at'>): Promise<AccountReconciliation> => {
    const id = `rec-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    // Also update actual_balance on the account
    await supabase
      .from('money_accounts')
      .update({ actual_balance: rec.actual_balance })
      .eq('id', rec.account_id);

    const { data, error } = await supabase
      .from('account_reconciliations')
      .insert([{ ...rec, id }])
      .select()
      .single();
    if (error) throw error;

    // If there's a difference, create a manual adjustment transaction
    if (rec.difference !== 0) {
      const adjId = `ftx-adj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      await supabase.from('financial_transactions').insert([{
        id: adjId,
        account_id: rec.account_id,
        transaction_date: rec.reconciliation_date,
        description: `Ajuste de conciliación: ${rec.notes || 'Diferencia detectada'}`,
        transaction_type: 'manual_adjustment',
        amount_in: rec.difference > 0 ? rec.difference : 0,
        amount_out: rec.difference < 0 ? Math.abs(rec.difference) : 0,
        reference_id: id,
        reference_type: 'reconciliation',
        notes: rec.notes,
      }]);
    }

    return data as AccountReconciliation;
  },

  // ── Capital Entries ───────────────────────────────────────────────────────

  getCapitalEntries: async (): Promise<CapitalEntry[]> => {
    const { data, error } = await supabase
      .from('capital_entries')
      .select('*')
      .order('entry_date', { ascending: false });
    if (error) throw error;
    return (data || []) as CapitalEntry[];
  },

  createCapitalEntry: async (entry: Omit<CapitalEntry, 'id' | 'created_at'>): Promise<CapitalEntry> => {
    const id = `cap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('capital_entries')
      .insert([{ ...entry, id }])
      .select()
      .single();
    if (error) throw error;

    // Create corresponding financial transaction ONLY if:
    // 1. An account is specified AND
    // 2. The funding source is NOT external/family (external funding never touches Bunori accounts)
    // 3. The entry_source is NOT 'target' (investment targets are planning-only, never create cash)
    const isExternalFunding = entry.entry_source === 'external_family';
    const isTargetOnly = entry.entry_source === 'target';
    if (entry.account_id && !isExternalFunding && !isTargetOnly) {
      const txId = `ftx-cap-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      let txType = 'capital_contribution';
      let amountIn = 0;
      let amountOut = 0;
      let txDesc = entry.description;
      if (entry.entry_type === 'contribution') {
        txType = 'capital_contribution';
        amountIn = entry.amount;
        txDesc = txDesc || 'Aporte de capital';
      } else if (entry.entry_type === 'withdrawal') {
        txType = 'owner_withdrawal';
        amountOut = entry.amount;
        txDesc = txDesc || 'Retiro del propietario';
      } else if (entry.entry_type === 'capital_return') {
        // Capital return: money goes OUT of the business account back to the owner
        txType = 'owner_withdrawal';
        amountOut = entry.amount;
        txDesc = txDesc || 'Retorno de capital al propietario';
      }
      await supabase.from('financial_transactions').insert([{
        id: txId,
        account_id: entry.account_id,
        transaction_date: entry.entry_date,
        description: txDesc,
        transaction_type: txType,
        amount_in: amountIn,
        amount_out: amountOut,
        reference_id: id,
        reference_type: 'capital_entry',
        notes: entry.notes,
      }]);
    }

    return data as CapitalEntry;
  },

  deleteCapitalEntry: async (id: string): Promise<void> => {
    await supabase.from('financial_transactions').delete().eq('reference_id', id);
    const { error } = await supabase.from('capital_entries').delete().eq('id', id);
    if (error) throw error;
  },

  // ── Business Summary ──────────────────────────────────────────────────────

  getBusinessSummary: async () => {
    const [accounts, txs, capitalEntries, ingredients, capitalSettings] = await Promise.all([
      supabase.from('money_accounts').select('*'),
      supabase.from('financial_transactions').select('*'),
      supabase.from('capital_entries').select('*'),
      supabase.from('ingredients').select('stock, unit_cost'),
      supabase.from('capital_investment_settings').select('*').eq('id', 'singleton').maybeSingle(),
    ]);

    const accountList = (accounts.data || []) as MoneyAccount[];
    const txList = (txs.data || []) as FinancialTransaction[];
    const capitalList = (capitalEntries.data || []) as CapitalEntry[];

    // Calculate expected balance per account
    // IMPORTANT: Only real financial transactions affect account balances.
    // Capital target and external funding do NOT add to account balances.
    const expectedBalances: Record<string, number> = {};
    accountList.forEach((acc) => {
      expectedBalances[acc.id] = Number(acc.opening_balance);
    });
    txList.forEach((tx) => {
      if (expectedBalances[tx.account_id] !== undefined) {
        expectedBalances[tx.account_id] += Number(tx.amount_in) - Number(tx.amount_out);
      }
    });

    // REAL CASH = only actual Bunori account balances (BAC, Atlántida, Caja, etc.)
    // Capital/investment is NEVER included in real cash
    const totalRealCash = accountList
      .filter((a) => a.active)
      .reduce((s, a) => s + (expectedBalances[a.id] || 0), 0);

    // Investment target (planning concept, NOT cash)
    const investmentTarget = Number(capitalSettings.data?.investment_target || 0);

    // ── SINGLE SOURCE OF TRUTH FOR CAPITAL INVESTED ──────────────────────────
    // Capital Invested is calculated ONLY from capital_entries table.
    // When an expense/purchase funded by Capital de inversión is created, a capital_entry
    // is created (cap-exp-{id} or cap-mig-po-{id}). When deleted, that entry is also deleted.
    // This prevents orphaned capital movements from inflating the total.
    //
    // We do NOT read from operational_expenses or purchase_orders tables for capital calculation
    // because those tables may contain deleted/cancelled records that would double-count.
    const totalCapitalInvested = capitalList
      .filter((c) => c.entry_type === 'contribution' && c.entry_source !== 'target')
      .reduce((s, c) => s + Number(c.amount), 0);

    // Capital withdrawn by owner
    const totalWithdrawn = capitalList
      .filter((c) => c.entry_type === 'withdrawal')
      .reduce((s, c) => s + Number(c.amount), 0);

    // Capital returned to owner (explicit capital return/distribution transactions)
    // This is the ONLY metric that counts as "capital recovered"
    // Inventory, cash inside the business, or business value do NOT count as capital returned
    const capitalReturned = capitalList
      .filter((c) => c.entry_type === 'capital_return')
      .reduce((s, c) => s + Number(c.amount), 0);

    // Net capital = invested - withdrawn
    const netCapital = totalCapitalInvested - totalWithdrawn;

    // Capital remaining to invest = target - invested
    const capitalRemaining = Math.max(0, investmentTarget - totalCapitalInvested);

    // External/family funded amount (never entered Bunori accounts)
    const externalFundingTotal = capitalList
      .filter((c) => c.entry_type === 'contribution' && c.entry_source === 'external_family')
      .reduce((s, c) => s + Number(c.amount), 0);

    // Inventory value
    const inventoryValue = (ingredients.data || []).reduce(
      (s: number, ing: { stock: number; unit_cost: number }) => s + Number(ing.stock) * Number(ing.unit_cost),
      0
    );

    // BUSINESS VALUE = Real Cash + Inventory + Other Assets - Liabilities
    // Do NOT add capital target or capital invested to business value
    const businessValue = totalRealCash + inventoryValue;

    // Capital Recovery = Capital actually returned to owner / Capital Invested
    // IMPORTANT: This is NOT businessValue / capitalInvested.
    // Capital inside the business (inventory, cash) is NOT "returned" to the owner.
    // Recovery only increases when the owner explicitly records a capital return/distribution.
    const capitalPendingRecovery = Math.max(0, totalCapitalInvested - capitalReturned);
    const capitalRecoveryPct = totalCapitalInvested > 0 ? (capitalReturned / totalCapitalInvested) * 100 : 0;

    return {
      accountList,
      expectedBalances,
      // Real cash (Bunori accounts only)
      totalMoneyAvailable: totalRealCash,
      totalRealCash,
      // Capital / Investment (separate from cash)
      investmentTarget,
      totalCapitalInvested,
      totalWithdrawn,
      capitalReturned,
      capitalPendingRecovery,
      netCapital,
      capitalRemaining,
      externalFundingTotal,
      // Legacy alias for backward compat
      totalContributed: totalCapitalInvested,
      // Assets
      inventoryValue,
      // Business value (real assets only, no capital double-counting)
      businessValue,
      capitalRecoveryPct,
    };
  },

  // ── Capital Investment Settings ───────────────────────────────────────────

  getInvestmentTarget: async (): Promise<number> => {
    const { data } = await supabase
      .from('capital_investment_settings')
      .select('investment_target')
      .eq('id', 'singleton')
      .maybeSingle();
    return Number(data?.investment_target || 0);
  },

  setInvestmentTarget: async (target: number, notes?: string): Promise<void> => {
    await supabase
      .from('capital_investment_settings')
      .upsert({ id: 'singleton', investment_target: target, notes: notes || '', updated_at: new Date().toISOString() });
  },

  /**
   * Reset all capital investment entries to zero.
   * Creates an audit record in capital_reset_audit.
   * Does NOT delete sales, purchases, expenses, products, or inventory.
   * Only removes capital_entries records so Capital Invested recalculates to L0.
   */
  resetCapitalInvestment: async (reason?: string): Promise<{ previousCapital: number; entriesDeleted: number }> => {
    // 1. Calculate current capital before reset
    const { data: entries } = await supabase
      .from('capital_entries')
      .select('*');
    const capitalList = (entries || []) as CapitalEntry[];
    const previousCapital = capitalList
      .filter((c) => c.entry_type === 'contribution' && c.entry_source !== 'target')
      .reduce((s, c) => s + Number(c.amount), 0);
    const entriesDeleted = capitalList.filter((c) => c.entry_source !== 'target').length;

    // 2. Delete all non-target capital entries
    // We keep 'target' entries (investment goal planning) but remove all real capital movements
    await supabase.from('capital_entries').delete().neq('entry_source', 'target');

    // 3. Also delete any financial transactions linked to capital entries
    // (capital contributions that created account movements)
    await supabase.from('financial_transactions').delete().eq('reference_type', 'capital_entry');

    // 4. Create audit record
    const auditId = `cra-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await supabase.from('capital_reset_audit').insert([{
      id: auditId,
      reset_at: new Date().toISOString(),
      previous_capital: previousCapital,
      new_capital: 0,
      entries_deleted: entriesDeleted,
      reason: reason || 'Reinicio manual de capital invertido',
      notes: 'Reinicio administrativo — los datos de ventas, compras, gastos e inventario no fueron modificados',
    }]);

    return { previousCapital, entriesDeleted };
  },

  /**
   * Get capital reset audit history
   */
  getCapitalResetAudit: async (): Promise<Array<{
    id: string;
    reset_at: string;
    previous_capital: number;
    new_capital: number;
    entries_deleted: number;
    reason: string;
    notes: string;
  }>> => {
    const { data, error } = await supabase
      .from('capital_reset_audit')
      .select('*')
      .order('reset_at', { ascending: false });
    if (error) return [];
    return (data || []) as Array<{
      id: string;
      reset_at: string;
      previous_capital: number;
      new_capital: number;
      entries_deleted: number;
      reason: string;
      notes: string;
    }>;
  },
};

// ─── Employees & Payroll ──────────────────────────────────────────────────────

export interface Employee {
  id: string;
  name: string;
  position: string;
  payment_type: string;
  salary_amount: number;
  payment_frequency: string;
  start_date: string;
  active: boolean;
  notes: string;
  created_at?: string;
}

export interface PayrollPayment {
  id: string;
  employee_id: string;
  pay_period_start: string;
  pay_period_end: string;
  payment_date: string;
  gross_amount: number;
  deductions: number;
  net_amount: number;
  payment_account_id?: string;
  status: 'pending' | 'paid' | 'cancelled';
  notes: string;
  created_at?: string;
  employee?: Employee;
  account?: MoneyAccount;
}

export const employeesService = {
  getAll: async (): Promise<Employee[]> => {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .order('name', { ascending: true });
    if (error) throw error;
    return (data || []) as Employee[];
  },

  create: async (emp: Omit<Employee, 'id' | 'created_at'>): Promise<Employee> => {
    const id = `emp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('employees')
      .insert([{ ...emp, id }])
      .select()
      .single();
    if (error) throw error;
    return data as Employee;
  },

  update: async (id: string, emp: Partial<Employee>): Promise<Employee> => {
    const { data, error } = await supabase
      .from('employees')
      .update(emp)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as Employee;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase.from('employees').delete().eq('id', id);
    if (error) throw error;
  },
};

export const payrollService = {
  getAll: async (): Promise<PayrollPayment[]> => {
    const { data, error } = await supabase
      .from('payroll_payments')
      .select('*, employee:employees(*), account:money_accounts(*)')
      .order('payment_date', { ascending: false });
    if (error) throw error;
    return (data || []) as PayrollPayment[];
  },

  create: async (payment: Omit<PayrollPayment, 'id' | 'created_at' | 'employee' | 'account'>): Promise<PayrollPayment> => {
    const id = `pay-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('payroll_payments')
      .insert([{ ...payment, id }])
      .select()
      .single();
    if (error) throw error;

    // Create financial transaction if paid
    if (payment.status === 'paid' && payment.payment_account_id) {
      const exists = await accountingService.transactionExistsForReference(id, 'payroll');
      if (!exists) {
        await accountingService.createTransaction({
          account_id: payment.payment_account_id,
          transaction_date: payment.payment_date,
          description: `Nómina pagada`,
          transaction_type: 'payroll',
          amount_in: 0,
          amount_out: payment.net_amount,
          reference_id: id,
          reference_type: 'payroll',
          notes: payment.notes,
        });
      }
    }

    return data as PayrollPayment;
  },

  markPaid: async (id: string, paymentAccountId: string, paymentDate: string): Promise<void> => {
    const { data: existing } = await supabase
      .from('payroll_payments')
      .select('net_amount, notes, status')
      .eq('id', id)
      .single();

    if (!existing || existing.status === 'paid') return;

    await supabase
      .from('payroll_payments')
      .update({ status: 'paid', payment_account_id: paymentAccountId, payment_date: paymentDate })
      .eq('id', id);

    const exists = await accountingService.transactionExistsForReference(id, 'payroll');
    if (!exists) {
      await accountingService.createTransaction({
        account_id: paymentAccountId,
        transaction_date: paymentDate,
        description: `Nómina pagada`,
        transaction_type: 'payroll',
        amount_in: 0,
        amount_out: Number(existing.net_amount),
        reference_id: id,
        reference_type: 'payroll',
        notes: existing.notes || '',
      });
    }
  },

  cancel: async (id: string): Promise<void> => {
    await supabase.from('payroll_payments').update({ status: 'cancelled' }).eq('id', id);
    await accountingService.deleteTransactionsByReference(id);
  },

  delete: async (id: string): Promise<void> => {
    await accountingService.deleteTransactionsByReference(id);
    const { error } = await supabase.from('payroll_payments').delete().eq('id', id);
    if (error) throw error;
  },

  getTotalPayroll: async (from?: string, to?: string): Promise<number> => {
    let query = supabase
      .from('payroll_payments')
      .select('net_amount')
      .eq('status', 'paid');
    if (from) query = query.gte('payment_date', from);
    if (to) query = query.lte('payment_date', to);
    const { data } = await query;
    return (data || []).reduce((s, p) => s + Number(p.net_amount), 0);
  },
};

// ─── Inventory Movements (Kardex) ────────────────────────────────────────────

export interface InventoryMovement {
  id: string;
  ingredient_id: string;
  ingredient_name: string;
  movement_type: string;
  quantity_in: number;
  quantity_out: number;
  unit: string;
  unit_cost: number;
  total_cost: number;
  balance_before: number;
  balance_after: number;
  movement_date: string;
  reference_id: string;
  reference_type: string;
  notes: string;
  created_at?: string;
}

export interface PhysicalInventoryCount {
  id: string;
  count_date: string;
  ingredient_id: string;
  ingredient_name: string;
  theoretical_stock: number;
  physical_stock: number;
  difference: number;
  unit: string;
  unit_cost: number;
  estimated_loss_value: number;
  loss_classification: string;
  confirmed: boolean;
  notes: string;
  created_at?: string;
}

export const kardexService = {
  getMovements: async (ingredientId?: string, from?: string, to?: string): Promise<InventoryMovement[]> => {
    let query = supabase
      .from('inventory_movements')
      .select('*')
      .order('movement_date', { ascending: false })
      .order('created_at', { ascending: false });
    if (ingredientId) query = query.eq('ingredient_id', ingredientId);
    if (from) query = query.gte('movement_date', from);
    if (to) query = query.lte('movement_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as InventoryMovement[];
  },

  recordMovement: async (mov: Omit<InventoryMovement, 'id' | 'created_at'>): Promise<InventoryMovement> => {
    const id = `mov-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('inventory_movements')
      .insert([{ ...mov, id }])
      .select()
      .single();
    if (error) throw error;
    return data as InventoryMovement;
  },

  getPhysicalCounts: async (from?: string, to?: string): Promise<PhysicalInventoryCount[]> => {
    let query = supabase
      .from('physical_inventory_counts')
      .select('*')
      .order('count_date', { ascending: false });
    if (from) query = query.gte('count_date', from);
    if (to) query = query.lte('count_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as PhysicalInventoryCount[];
  },

  createPhysicalCount: async (count: Omit<PhysicalInventoryCount, 'id' | 'created_at'>): Promise<PhysicalInventoryCount> => {
    const id = `cnt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('physical_inventory_counts')
      .insert([{ ...count, id }])
      .select()
      .single();
    if (error) throw error;

    // If confirmed and there's a difference, update ingredient stock and record movement
    if (count.confirmed && count.difference !== 0) {
      const { data: ing } = await supabase
        .from('ingredients').select('stock').eq('id', count.ingredient_id)
        .single();
      if (ing) {
        const newStock = count.physical_stock;
        await supabase.from('ingredients').update({ stock: newStock }).eq('id', count.ingredient_id);
        // Record kardex movement
        const movId = `mov-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        await supabase.from('inventory_movements').insert([{
          id: movId,
          ingredient_id: count.ingredient_id,
          ingredient_name: count.ingredient_name,
          movement_type: 'physical_count_adjustment',
          quantity_in: count.difference > 0 ? count.difference : 0,
          quantity_out: count.difference < 0 ? Math.abs(count.difference) : 0,
          unit: count.unit,
          unit_cost: count.unit_cost,
          total_cost: Math.abs(count.difference) * count.unit_cost,
          balance_before: count.theoretical_stock,
          balance_after: count.physical_stock,
          movement_date: count.count_date,
          reference_id: id,
          reference_type: 'physical_count',
          notes: `Conteo físico: ${count.loss_classification}. ${count.notes}`,
        }]);
      }
    }

    return data as PhysicalInventoryCount;
  },

  getInventoryValue: async (): Promise<number> => {
    const { data } = await supabase.from('ingredients').select('stock, unit_cost');
    return (data || []).reduce((s: number, ing: { stock: number; unit_cost: number }) =>
      s + Number(ing.stock) * Number(ing.unit_cost), 0);
  },
};

// ─── Liabilities ──────────────────────────────────────────────────────────────

export interface Liability {
  id: string;
  name: string;
  liability_type: string;
  amount: number;
  creditor: string;
  due_date: string;
  status: string;
  notes: string;
  created_at?: string;
}

export const liabilitiesService = {
  getAll: async (): Promise<Liability[]> => {
    const { data, error } = await supabase
      .from('liabilities')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []) as Liability[];
  },

  create: async (lib: Omit<Liability, 'id' | 'created_at'>): Promise<Liability> => {
    const id = `lib-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('liabilities')
      .insert([{ ...lib, id }])
      .select()
      .single();
    if (error) throw error;
    return data as Liability;
  },

  update: async (id: string, lib: Partial<Liability>): Promise<Liability> => {
    const { data, error } = await supabase
      .from('liabilities')
      .update(lib)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as Liability;
  },

  delete: async (id: string): Promise<void> => {
    const { error } = await supabase.from('liabilities').delete().eq('id', id);
    if (error) throw error;
  },

  getTotalActive: async (): Promise<number> => {
    const { data } = await supabase
      .from('liabilities')
      .select('amount')
      .eq('status', 'active');
    return (data || []).reduce((s, l) => s + Number(l.amount), 0);
  },
};

// ─── Closings System ──────────────────────────────────────────────────────────

export interface DailyClosing {
  id: string;
  closing_date: string;
  closed_at: string;
  closed_by?: string;
  total_sales: number;
  num_sales: number;
  total_direct_costs: number;
  total_packaging: number;
  total_commissions: number;
  total_absorbed_delivery: number;
  total_operating_expenses: number;
  total_payroll: number;
  gross_profit: number;
  net_profit: number;
  total_money_in: number;
  total_money_out: number;
  total_transfers: number;
  total_refunds: number;
  total_owner_withdrawals: number;
  total_purchases: number;
  closing_status: 'balanced' | 'differences_detected';
  notes?: string;
  created_at?: string;
  accounts?: DailyClosingAccount[];
}

export interface DailyClosingAccount {
  id: string;
  daily_closing_id: string;
  account_id: string;
  account_name: string;
  account_type: string;
  expected_balance: number;
  actual_balance?: number;
  difference?: number;
  status: 'balanced' | 'differences_detected' | 'pending';
  notes?: string;
}

export interface ClosingAdjustment {
  id: string;
  daily_closing_id?: string;
  monthly_closing_id?: string;
  account_id: string;
  account_name: string;
  adjustment_date: string;
  amount: number;
  direction: 'increase' | 'decrease';
  reason: string;
  notes?: string;
  status: 'pending' | 'applied' | 'dismissed';
  created_at?: string;
}

export interface MonthlyClosing {
  id: string;
  closing_year: number;
  closing_month: number;
  closed_at: string;
  closed_by?: string;
  total_sales: number;
  num_sales: number;
  total_ingredient_cost: number;
  total_packaging_cost: number;
  total_commissions: number;
  total_absorbed_delivery: number;
  gross_profit: number;
  total_operating_expenses: number;
  total_payroll: number;
  net_profit: number;
  net_margin: number;
  total_money_in: number;
  total_money_out: number;
  total_purchases: number;
  total_transfers: number;
  total_owner_contributions: number;
  total_owner_withdrawals: number;
  total_refunds: number;
  beginning_inventory_value: number;
  ending_inventory_value: number;
  inventory_purchases: number;
  inventory_consumed: number;
  inventory_losses: number;
  closing_status: 'balanced' | 'differences_detected';
  notes?: string;
  created_at?: string;
  accounts?: MonthlyClosingAccount[];
}

export interface MonthlyClosingAccount {
  id: string;
  monthly_closing_id: string;
  account_id: string;
  account_name: string;
  account_type: string;
  opening_balance: number;
  total_in: number;
  total_out: number;
  transfers_in: number;
  transfers_out: number;
  adjustments: number;
  expected_ending_balance: number;
  actual_ending_balance?: number;
  difference?: number;
  status: 'balanced' | 'differences_detected' | 'pending';
  notes?: string;
}

export const closingsService = {
  // ── Daily Closings ────────────────────────────────────────────────────────

  getDailyClosing: async (date: string): Promise<DailyClosing | null> => {
    const { data, error } = await supabase
      .from('daily_closings')
      .select('*, accounts:daily_closing_accounts(*)')
      .eq('closing_date', date)
      .maybeSingle();
    if (error) throw error;
    return data as DailyClosing | null;
  },

  getDailyClosings: async (from?: string, to?: string): Promise<DailyClosing[]> => {
    let query = supabase
      .from('daily_closings')
      .select('*')
      .order('closing_date', { ascending: false });
    if (from) query = query.gte('closing_date', from);
    if (to) query = query.lte('closing_date', to);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as DailyClosing[];
  },

  createDailyClosing: async (
    closing: Omit<DailyClosing, 'id' | 'created_at' | 'accounts'>,
    accountSnapshots: Omit<DailyClosingAccount, 'id' | 'daily_closing_id'>[]
  ): Promise<DailyClosing> => {
    // Prevent duplicates
    const existing = await closingsService.getDailyClosing(closing.closing_date);
    if (existing) throw new Error(`Ya existe un cierre para el ${closing.closing_date}`);

    const id = `dc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('daily_closings')
      .insert([{ ...closing, id }])
      .select()
      .single();
    if (error) throw error;

    if (accountSnapshots.length > 0) {
      const rows = accountSnapshots.map((a) => ({
        ...a,
        id: `dca-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        daily_closing_id: id,
      }));
      await supabase.from('daily_closing_accounts').insert(rows);
    }

    return data as DailyClosing;
  },

  // ── Closing Adjustments ───────────────────────────────────────────────────

  getAdjustments: async (dailyClosingId?: string): Promise<ClosingAdjustment[]> => {
    let query = supabase
      .from('closing_adjustments')
      .select('*')
      .order('created_at', { ascending: false });
    if (dailyClosingId) query = query.eq('daily_closing_id', dailyClosingId);
    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as ClosingAdjustment[];
  },

  createAdjustment: async (adj: Omit<ClosingAdjustment, 'id' | 'created_at'>): Promise<ClosingAdjustment> => {
    const id = `cadj-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('closing_adjustments')
      .insert([{ ...adj, id }])
      .select()
      .single();
    if (error) throw error;
    return data as ClosingAdjustment;
  },

  // ── Monthly Closings ──────────────────────────────────────────────────────

  getMonthlyClosing: async (year: number, month: number): Promise<MonthlyClosing | null> => {
    const { data, error } = await supabase
      .from('monthly_closings')
      .select('*, accounts:monthly_closing_accounts(*)')
      .eq('closing_year', year)
      .eq('closing_month', month)
      .maybeSingle();
    if (error) throw error;
    return data as MonthlyClosing | null;
  },

  getMonthlyClosings: async (): Promise<MonthlyClosing[]> => {
    const { data, error } = await supabase
      .from('monthly_closings')
      .select('*')
      .order('closing_year', { ascending: false })
      .order('closing_month', { ascending: false });
    if (error) throw error;
    return (data || []) as MonthlyClosing[];
  },

  createMonthlyClosing: async (
    closing: Omit<MonthlyClosing, 'id' | 'created_at' | 'accounts'>,
    accountSnapshots: Omit<MonthlyClosingAccount, 'id' | 'monthly_closing_id'>[]
  ): Promise<MonthlyClosing> => {
    const existing = await closingsService.getMonthlyClosing(closing.closing_year, closing.closing_month);
    if (existing) throw new Error(`Ya existe un cierre para ${closing.closing_month}/${closing.closing_year}`);

    const id = `mc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const { data, error } = await supabase
      .from('monthly_closings')
      .insert([{ ...closing, id }])
      .select()
      .single();
    if (error) throw error;

    if (accountSnapshots.length > 0) {
      const rows = accountSnapshots.map((a) => ({
        ...a,
        id: `mca-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        monthly_closing_id: id,
      }));
      await supabase.from('monthly_closing_accounts').insert(rows);
    }

    return data as MonthlyClosing;
  },
};
